import React, { useState, useEffect } from 'react';

// --- Card Data (Updated with Reviews) ---
const cardData = [
  {
    index: 0,
    name: 'Alonso Coronado',
    country: 'Costa Rica',
    review: 'Techvelly built a high-quality website for my business and made every change I requested. They really cared about my satisfaction, and the price was totally worth it. Highly recommend their service!',
  },
  {
    index: 1,
    name: 'Leo Valmont',
    country: 'Portugal',
    review: 'Working with Techvelly was an amazing experience. They went above and beyond, stayed in touch through every step, and handled all my requests with real care and professionalism. I’m truly grateful for their support and would gladly work with them again!',
  },
  {
    index: 2,
    name: 'Grace',
    country: 'Canada',
    review: 'I hired Techvelly after seeing their reviews, and they exceeded my expectations. Fast, professional, and great communication throughout — a pleasure to work with!',
  },
  {
    index: 3,
    name: 'Barbara',
    country: 'Usa',
    review: 'It was a pleasure working with the Techvelly Team. They delivered exactly what I wanted — a professional design, clear communication, and great attention to detail. I appreciate their patience and ongoing support. Highly recommended!',
  },
  {
    index: 4,
    name: 'Pedro Benton',
    country: 'Canada',
    review: 'Great experience! Communication was smooth, and the website turned out even better than we hoped. Excellent work by the Techvelly team!',
  },
  {
    index: 5,
    name: 'Ashley',
    country: 'UK',
    review: 'Huge thanks to the Techvelly team for the excellent work! Great quality, fast delivery, and clear communication throughout. I’m very happy with the result and appreciate the ongoing support. Highly recommended!',
  },
  {
    index: 6,
    name: 'iryna vezh',
    country: 'United States',
    review: 'Amazing work! The Techvelly team was patient, super fast, and delivered everything perfectly. I’ll definitely recommend them to others!',
  },
  {
    index: 7,
    name: 'justin_b_lane',
    country: 'United States',
    review: 'Fantastic experience! Communication was quick, updates were done instantly, and I was informed every step of the way. I’ll definitely work with Techvelly again!',
  },
];

/**
 * A component to inject the dynamic CSS for card positioning and animations.
 * This is the cleanest way to replicate the CSS variable and data-state
 * logic from the original HTML file within a React component.
 */
const CardSliderStyles = () => (
  <style>{`
    :root {
      --card-height: 400px;
      --card-width: 260px; /* Fixed width for better control */
      --focus-scale: 1;
      --side-scale: 0.75;
      --side-blur: 3px;
      --side-opacity: 0.6;
      --side-translate-x: 100px; /* Reduced for tighter grouping */
      --side-translate-z: -150px;
      --hidden-scale: 0.5;
      --hidden-translate-x: 200px;
      --hidden-translate-z: -300px;
      --hidden-opacity: 0;
      --transition-speed: 0.5s;
      --slider-offset-x: 0%; /* Center the slider in its container */
    }

    /* Card Positioning States */
    .card[data-state="focus"] {
      transform: translateX(var(--slider-offset-x)) translateZ(0) scale(var(--focus-scale));
      opacity: 1;
      filter: blur(0);
      z-index: 10;
    }
    
    .card[data-state="right"] {
      transform: translateX(calc(var(--slider-offset-x) + var(--side-translate-x))) translateZ(var(--side-translate-z)) scale(var(--side-scale));
      opacity: var(--side-opacity);
      filter: blur(var(--side-blur));
      z-index: 5;
    }
    
    .card[data-state="left"] {
      /* Adjust left cards to respect the new slider offset */
      transform: translateX(calc(var(--slider-offset-x) - var(--side-translate-x))) translateZ(var(--side-translate-z)) scale(var(--side-scale));
      opacity: var(--side-opacity);
      filter: blur(var(--side-blur));
      z-index: 5;
    }
    
    .card[data-state="far-right"] {
      transform: translateX(calc(var(--slider-offset-x) + var(--hidden-translate-x))) translateZ(var(--hidden-translate-z)) scale(var(--hidden-scale));
      opacity: var(--hidden-opacity);
      z-index: 1;
    }
    
    .card[data-state="far-left"] {
      transform: translateX(calc(var(--slider-offset-x) - var(--hidden-translate-x))) translateZ(var(--hidden-translate-z)) scale(var(--hidden-scale));
      opacity: var(--hidden-opacity);
      z-index: 1;
    }

    /* Responsive Adjustments */
    @media (max-width: 768px) {
      :root {
        --card-height: 350px;
        --side-translate-x: 70px;
        --side-translate-z: -100px;
        --hidden-translate-x: 140px;
        --hidden-translate-z: -200px;
        --slider-offset-x: 0%; /* Center on small screens */
      }
    }
    
    @media (max-width: 480px) {
      :root {
        --card-height: 320px;
        --card-width: 220px;
        --side-translate-x: 50px;
        --side-translate-z: -80px;
        --hidden-translate-x: 100px;
        --hidden-translate-z: -160px;
      }
    }
  `}</style>
);

/**
 * Calculates the state of a card based on its index and the current focused index.
 * This is a direct port of the logic from the original JavaScript.
 */
const getCardState = (index, currentIndex, totalCards) => {
  const offset = (index - currentIndex + totalCards) % totalCards;
  
  switch (offset) {
    case 0:
      return 'focus';
    case 1:
      return 'right';
    case totalCards - 1: // This is the item to the left
      return 'left';
    case 2:
      return 'far-right';
    case totalCards - 2:
      return 'far-left';
    default:
      // All other cards are further away and hidden
      return offset < totalCards / 2 ? 'far-right' : 'far-left';
  }
};

// --- Reusable Star Icon Component ---
const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-yellow-300"> {/* Changed to text-yellow-300 */}
    <path fillRule="evenodd" d="M10.868 2.884c.321-.662 1.135-.662 1.456 0l1.838 3.791 4.183.608c.73.106 1.022.998.494 1.508l-3.027 2.95 .715 4.165c.124.727-.635 1.285-1.29.944L10 15.111l-3.738 1.965c-.655.341-1.414-.217-1.29-.944l.715-4.165-3.027-2.95c-.528-.51-.236-1.402.494-1.508l4.183-.608L8.544 2.884z" clipRule="evenodd" />
  </svg>
);


// --- The Main App Component ---
const ReviewSection = () =>  {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalCards = cardData.length;

  // Automatic slider advancement
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % totalCards);
    }, 4000); // Change card every 4 seconds

    return () => clearInterval(interval); // Clean up the interval on component unmount
  }, [totalCards]); // Re-run effect if totalCards changes (though it's static here)

  const handleCardClick = (index) => {
    setCurrentIndex(index);
  };

  return (
    <>
      {/* Inject the dynamic CSS into the document head (or here) */}
      <CardSliderStyles />

      {/* Main container with Tailwind classes - UPDATED LAYOUT */}
      <main className="py-20 bg-gradient-to-bl from-white to-[#e0f2f1] font-inter overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* --- Left Side: Text Content (Grid Column 1) --- */}
            <div className="flex flex-col justify-center z-20">
              {/* --- Updated Heading --- */}
              <h1 className="text-3xl font-bold leading-tight bg-gradient-to-r from-[#22577a] to-[#57c5b6] bg-clip-text text-transparent pb-2">
                What Our Clients Say
              </h1>
              {/* --- Added Decorative Underline --- */}
              <div className="w-24 h-1.5 bg-gradient-to-r from-[#22577a] to-[#57c5b6] mt-3 mb-6 rounded-full"></div>

              <p className="text-base text-gray-600 mt-2">
                We take pride in building solutions that our clients love. But don't just take our word for it—read what they have to say about their experience working with us.
              </p>
              {/* --- Added more text as requested --- */}
              <p className="text-base text-gray-600 mt-4">
                Our process is built on collaboration and transparency. From the first consultation to the final launch, we ensure you're part of the journey. This commitment to partnership is why we're able to deliver results that don't just meet expectations, but exceed them.
              </p>
              {/* --- Updated Tagline --- */}
              <p className="text-base text-gray-600 mt-8 font-medium">
                "Real reviews from real partners."
              </p>
            </div>

            {/* --- Right Section: Card Slider (Grid Column 2) --- */}
            <div 
              className="relative w-full flex items-center justify-center" // Removed lg:w-1/2, grid handles width
              style={{ minHeight: 'var(--card-height)' }} // Ensure container has height
            >
              <div 
                className="relative w-full"
                style={{ 
                  height: 'var(--card-height)',
                  perspective: '1500px',
                  transformStyle: 'preserve-3d', // Ensures children respect the 3D space
                }}
              >
                {cardData.map((card, index) => (
                  <div
                    key={card.index}
                    // Base card styling with Tailwind
                    className="card absolute left-0 right-0 mx-auto p-6 md:p-8 rounded-3xl
                                flex flex-col justify-between cursor-pointer
                                transition-all duration-500 ease-in-out
                                bg-gradient-to-tr from-[#22577a] via-[#387c9d] to-[#57c5b6] backdrop-blur-xl border border-white/18 shadow-2xl shadow-black/30"
                    style={{
                      width: 'var(--card-width)',
                      height: 'var(--card-height)',
                    }}
                    // Set the data-state attribute dynamically
                    data-state={getCardState(index, currentIndex, totalCards)}
                    onClick={() => handleCardClick(index)} // Card click still changes focus
                  >
                    {/* --- New Card Content --- */}
                    <div className="flex flex-col flex-grow text-white"> {/* Added text-white here for card content */}
                      <h3 className="text-xl md:text-2xl font-bold text-shadow-sm">
                        {card.name}
                      </h3>
                      <p className="text-sm font-light text-gray-200 mb-2">
                        {card.country}
                      </p>
                      <div className="flex mb-3">
                        <StarIcon />
                        <StarIcon />
                        <StarIcon />
                        <StarIcon />
                        <StarIcon />
                      </div>
                      <p className="text-sm md:text-base font-light leading-relaxed flex-grow italic">
                        "{card.review}"
                      </p>
                    </div>
                    {/* Removed "View Project" button */}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </main>
    </>
  );
}
export default ReviewSection;

